import { useEffect, useState } from "react";
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormHelperText,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import { get, isNil } from "lodash";
import { Box } from "@mui/system";

import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  ApplicableDocuments,
  DocumentAttachements,
  DocumentUploadActionDispatchTypes,
} from "../../../constants/DocumentUpload";
import FileUpload from "../../../components/formComponents/FileUpload";
import {
  FiberManualRecordIconStyle,
  DocGridBody,
  ListItemStyle,
  DocumentTypeIcon,
  DividerStyle,
  DocFlexRow,
} from "../styles/style";
import { flexAllCentre, flexRow } from "../../../styles/mui/styles/display";
import { RiDeleteIcon } from "../../../components/formComponents/styles/style";
import { DocumentUploadResponse } from "../../../models/Api/TabSessionStorage";
import { documentMaxSize, documentMinSize } from "../../../constants/Config";
import { StoredFileData, TabData } from "../../../models/TabSessionStorage";
import { rules } from "../../../utils/validations/Validations";
import { extractDocType, getValue } from "../../../utils";
import { getDocTypeIcon } from "../../../utils/DocIcons";

export interface PropsFromState {
  UploadedDocumentData: DocumentUploadResponse;
  setValue: UseFormSetValue<any>;
  control: Control<TabData, any>;
}

export interface PropsFromDispatch {
  UploadDocument: (file: any) => void;
  resetStates: (actionType: string[]) => void;
  DeleteDocument: (blobId: number) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const DocumentUpload: React.FC<AllProps> = ({
  UploadDocument,
  UploadedDocumentData,
  setValue,
  resetStates,
  control,
  DeleteDocument,
}: AllProps) => {
  const [files, setFiles] = useState<any>([]);
  const [sessionDataFileName, setSessionDatafileName] = useState<
    StoredFileData[]
  >([]);
  const maxFileSizeInMegaBytes = documentMaxSize.split("MB")[0];
  const maxFileSizeInBytes = Number(maxFileSizeInMegaBytes) * 1000000;
  const minFileSize = Number(documentMinSize.split("KB")[0]);

  useEffect(() => {
    if (files.length > 0) {
      const formData = new FormData();
      formData.append("files", files[0]);
      UploadDocument(formData);
    }
  }, [files]);

  useEffect(() => {
    const sessionFileData = sessionStorage.getItem("ReferralRequest");
    let FileDataFromSessionStorage: any = null;
    FileDataFromSessionStorage = JSON.parse(sessionFileData as string);
    if (
      Object.keys(FileDataFromSessionStorage).includes("referralBlobDetail") &&
      !isNil(FileDataFromSessionStorage) &&
      FileDataFromSessionStorage.referralBlobDetail !== null &&
      FileDataFromSessionStorage.referralBlobDetail.length !== 0
    ) {
      setSessionDatafileName([
        ...getValue(FileDataFromSessionStorage, "referralBlobDetail"),
      ]);
      setValue(
        "referralBlobDetail",
        getValue(FileDataFromSessionStorage, "referralBlobDetail")
      );
    }
    return () => {
      resetStates([DocumentUploadActionDispatchTypes.DOCUMENT_UPLOAD_RESET]);
    };
  }, []);

  useEffect(() => {
    if (get(UploadedDocumentData, "response[0].blobId") !== -1) {
      const storedFileData = {
        blobId: get(UploadedDocumentData, "response[0].blobId"),
        originalDocumentName: get(
          UploadedDocumentData,
          "response[0].originalFileName"
        ),
        blobContentLength: get(
          UploadedDocumentData,
          "response[0].blobContentLength"
        ),
        mediaType: get(UploadedDocumentData, "response[0].mediaType"),
      };
      setSessionDatafileName((prevData) => [...prevData, storedFileData]);
      setValue("referralBlobDetail", storedFileData);
    }
    return () => {
      resetStates([DocumentUploadActionDispatchTypes.DOCUMENT_UPLOAD_RESET]);
    };
  }, [UploadedDocumentData]);

  useEffect(() => {
    if (sessionDataFileName.length > 0) {
      setValue("referralBlobDetail", sessionDataFileName);
    }
  }, [sessionDataFileName]);

  const handleDeleteOp = (blobId: number) => {
    const updatedSessionData = sessionDataFileName.filter(
      (data) => data.blobId !== blobId
    );
    setSessionDatafileName(updatedSessionData);
    setValue("referralBlobDetail", updatedSessionData);
    DeleteDocument(blobId);
  };

  return (
    <>
      <Grid container pt={"1rem"}>
        <Grid size={{ xs: 7 }}>
          <Typography
            variant="h6"
            fontWeight={fontWeight.Weight[5]}
            color={colors.fonts[20]}
          >
            Please submit the following as attachements:
          </Typography>
          <List>
            {DocumentAttachements.map((doc) => (
              <ListItem key={doc}>
                <ListItemIcon sx={ListItemStyle}>
                  <FiberManualRecordIconStyle />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      fontWeight={fontWeight.Weight[3]}
                      color={colors.black[13]}
                    >
                      {doc}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Grid>
        <Grid size={{ xs: 5 }}>
          <Typography
            variant="h6"
            fontWeight={fontWeight.Weight[5]}
            color={colors.fonts[20]}
          >
            Please provide the following, as applicable:
          </Typography>
          <List>
            {ApplicableDocuments.map((doc) => (
              <ListItem key={doc}>
                <ListItemIcon sx={ListItemStyle}>
                  <FiberManualRecordIconStyle />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      fontWeight={fontWeight.Weight[3]}
                      color={colors.black[13]}
                    >
                      {doc}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>
      <Grid container pb={"0.1rem"} pt={"3rem"} sx={flexAllCentre}>
        <Grid size={{ xs: 4 }} sx={DocGridBody}>
          <Controller
            name={"referralBlobDetail.0.originalDocumentName"}
            control={control}
            defaultValue={""}
            rules={rules.Document}
            render={({ fieldState: { error } }) => {
              return (
                <>
                  <FileUpload
                    files={files}
                    setFiles={setFiles}
                    maxFileSizeInBytes={maxFileSizeInBytes}
                    type={1}
                    minFileSize={minFileSize}
                  />
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </>
              );
            }}
          />
        </Grid>
      </Grid>
      <Grid container pb={"2rem"} pl={"10rem"} sx={flexAllCentre}>
        <Grid size={{ xs: 6 }} sx={DocFlexRow}>
          {sessionDataFileName &&
            sessionDataFileName.map((data, index) => (
              <Box key={data.blobId} sx={flexRow}>
                <Box sx={DocumentTypeIcon}>
                  {getDocTypeIcon(extractDocType(data.mediaType))}
                </Box>
                <Box pt={"0.2rem"}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={fontWeight.Weight[4]}
                    color={colors.fonts[4]}
                  >
                    {data.originalDocumentName}
                  </Typography>
                </Box>
                <Box pl={"1rem"}>
                  <RiDeleteIcon onClick={() => handleDeleteOp(data.blobId)} />
                </Box>
                {sessionDataFileName.length - 1 !== index && (
                  <Divider sx={DividerStyle} orientation="vertical" flexItem />
                )}
              </Box>
            ))}
        </Grid>
      </Grid>
    </>
  );
};

export default DocumentUpload;
